import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

const LINKS = [
  { to: "/growth-partnership", label: "Growth Partnership" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 pointer-events-none"
      data-testid="main-nav"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 pointer-events-auto">
        <div
          className={`relative rounded-full h-16 md:h-[68px] pl-5 pr-3 md:pl-7 md:pr-4 flex items-center justify-between gvg-glass-pill ${
            scrolled ? "gvg-glass-pill-scrolled" : ""
          }`}
        >
        <Link
          to="/"
          className="group flex items-baseline gap-2 relative shrink-0 overflow-visible gvg-wordmark-idle"
          data-testid="nav-logo"
          aria-label="Get Virtual Growth home"
        >
          {/* Soft breathing halo behind the wordmark (hover only) */}
          <span aria-hidden className="gvg-halo" />

          {/* Horizontal scan-beam — razor-thin champagne line that passes through every 8s */}
          <span aria-hidden className="gvg-scanline" />

          {/* "Get Virtual" — ivory serif, staggered letter reveal with subtle scale */}
          <span className="relative flex items-baseline font-serif text-2xl md:text-[26px] leading-none tracking-tight text-ink">
            {"Get Virtual".split("").map((ch, i) => (
              <motion.span
                key={`gv-${i}`}
                initial={{ opacity: 0, y: 14, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.7,
                  delay: 0.12 + i * 0.038,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="inline-block"
                style={{ whiteSpace: "pre" }}
              >
                {ch}
              </motion.span>
            ))}
          </span>

          {/* "Growth" — italic serif in champagne with gentle breathing sheen */}
          <motion.span
            className="relative font-serif italic text-2xl md:text-[26px] leading-none tracking-tight text-forest gvg-shimmer"
            initial={{ opacity: 0, y: 16, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
          >
            Growth
          </motion.span>

          {/* Upward arrow with a fading afterimage trail — Amazon-swoop pointing up-right */}
          <motion.span
            aria-hidden
            className="relative inline-block self-end mb-1.5 ml-0.5 gvg-arrow-wrap"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Ghost afterimage — slightly offset, blurred, fades in/out */}
            <svg
              className="gvg-arrow-ghost text-brass"
              width="17"
              height="15"
              viewBox="0 0 17 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M 1.5 12.5 Q 7 11.5, 14 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d="M 10.2 2.8 L 14.5 2.2 L 14 6.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>

            {/* Live arrow */}
            <svg
              className="gvg-arrow-lift text-brass block"
              width="17"
              height="15"
              viewBox="0 0 17 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <motion.path
                d="M 1.5 12.5 Q 7 11.5, 14 3.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.9, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
              />
              <motion.path
                d="M 10.2 2.8 L 14.5 2.2 L 14 6.8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ opacity: 0, y: 3, scale: 0.6 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, delay: 2.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: "14px 3px" }}
              />
            </svg>
          </motion.span>

          {/* Underline sweep on hover */}
          <motion.span
            aria-hidden
            className="absolute -bottom-1.5 left-0 h-px bg-gradient-to-r from-transparent via-brass to-transparent"
            initial={{ width: 0, opacity: 0 }}
            whileHover={{ width: "100%", opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`gvg-nav-link font-sans text-[13px] tracking-wide hover:text-forest inline-flex items-center gap-1.5 ${
                location.pathname === l.to ? "text-forest" : "text-ink-muted"
              }`}
            >
              {l.label}
              {l.preview && (
                <span
                  aria-label="Preview only"
                  data-testid={`nav-preview-chip-${l.label.toLowerCase()}`}
                  className="inline-flex items-center rounded-full border border-brass/50 text-brass text-[8px] uppercase tracking-mega px-1.5 py-0.5 leading-none"
                >
                  Preview
                </span>
              )}
              {location.pathname === l.to && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-2 left-0 right-0 h-px bg-brass"
                />
              )}
            </Link>
          ))}

          {/* Divider before the exclusive tier */}
          <span aria-hidden className="w-px h-4 bg-brass/25 ml-1" />
        </nav>

        <button
          className="lg:hidden flex flex-col gap-1.5 p-2 mr-1"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          <span className={`h-px w-6 bg-brass transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`h-px w-6 bg-brass transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-px w-6 bg-brass transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
        </div>
      </div>

      {open && (
        <motion.nav
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="lg:hidden overflow-hidden mx-4 mt-2 rounded-3xl gvg-glass-pill pointer-events-auto max-w-6xl md:mx-auto"
        >
          <div className="px-6 py-8 flex flex-col gap-1">
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className={`group flex items-center justify-between py-3 border-b border-line/60 font-serif text-2xl transition-colors duration-300 ${
                    location.pathname === l.to ? "text-forest" : "text-ink hover:text-forest"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    {l.label}
                    {l.preview && (
                      <span
                        aria-label="Preview only"
                        className="inline-flex items-center rounded-full border border-brass/50 text-brass text-[9px] uppercase tracking-mega px-1.5 py-0.5 leading-none"
                      >
                        Preview
                      </span>
                    )}
                  </span>
                  <span className="font-sans text-[10px] uppercase tracking-mega text-brass opacity-0 group-hover:opacity-100 transition-opacity">
                    Open &rarr;
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}
